// ** Modules
import React from 'react';

// ** UI
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

// ** Components
import { Icon } from '@/components/common/icons';

// ** Types
import type { SxProps, Theme } from '@mui/material/styles';

const IMAGE_EXTS = new Set(['JPG', 'JPEG', 'PNG', 'GIF', 'WEBP', 'AVIF', 'BMP', 'SVG']);

export type FileCardItem = {
	date: string;
	documentId: string;
	fileName: string;
	description?: string;
	url: string;
	previewUrl?: string;
	updatedAt?: string;
	ext?: string;
};

export type FileCardProps = {
	item: FileCardItem;
	selected?: boolean;
	onSelectedChange?: (selected: boolean, item: FileCardItem) => void;
	onDownload?: (item: FileCardItem) => void;
	onPreview?: (item: FileCardItem) => void;
	showPreview?: boolean;
	sx?: SxProps<Theme>;
};

export const downloadByUrl = (downloadUrl: string, fileName: string) => {
	const a = document.createElement('a');
	a.href = downloadUrl;
	a.download = fileName;
	a.rel = 'noopener';
	document.body.appendChild(a);
	a.click();
	a.remove();
};

const FileCard = ({
	item,
	selected,
	onSelectedChange,
	onDownload,
	onPreview,
	showPreview = false,
	sx
}: FileCardProps) => {
	const theme = useTheme();
	const [internalSelected, setInternalSelected] = React.useState(false);

	const isSelected = selected ?? internalSelected;

	const extension = React.useMemo(() => {
		if (item.ext) return item.ext.replace('.', '').toUpperCase();
		const fileName = item.fileName;
		const raw = fileName.split('.').pop();
		return raw ? raw.toUpperCase() : 'DATEI';
	}, [item.ext, item.fileName, item]);

	const uploadedDate = React.useMemo(() => {
		if (!item.date) return '';
		const parsed = new Date(item.date);
		if (Number.isNaN(parsed.getTime())) return item.date;
		return new Intl.DateTimeFormat('de-DE').format(parsed);
	}, [item.date]);

	const metaLine = uploadedDate ? `${extension} | ${uploadedDate}` : extension;
	const isImageFile = IMAGE_EXTS.has(extension);

	const handleSelectedChange = (next: boolean) => {
		if (onSelectedChange) onSelectedChange(next, item);
		else setInternalSelected(next);
	};

	const handleDownload = () => {
		if (onDownload) {
			onDownload(item);
			return;
		}
		downloadByUrl(item.url, item.fileName);
	};

	const handlePreview = () => {
		if (onPreview) {
			onPreview(item);
			return;
		}
		window.open(item.previewUrl ?? item.url, '_blank', 'noopener,noreferrer');
	};

	// Shared: hidden native input (identical in all three checkbox usages)
	const hiddenCheckboxInput = (
		<input
			type="checkbox"
			checked={isSelected}
			onChange={(e) => handleSelectedChange(e.target.checked)}
			aria-label={`${item.fileName} auswählen`}
			style={{
				position: 'absolute',
				opacity: 0,
				width: 0,
				height: 0,
				margin: 0
			}}
		/>
	);

	// Shared: visual checkbox
	const checkboxVisual = (
		<Box
			component="span"
			sx={{
				display: 'block',
				flexShrink: 0,
				width: 18,
				height: 18,
				borderRadius: '4px',
				border: isSelected ? 'none' : '1.4px solid #353535',
				backgroundColor: isSelected ? theme.palette.primary.main : 'transparent',
				boxSizing: 'border-box',
				position: 'relative'
			}}
		>
			{isSelected && (
				<svg
					viewBox="0 0 18 18"
					width="18"
					height="18"
					style={{ display: 'block', position: 'absolute', top: 0, left: 0 }}
				>
					<title>{`${item.fileName} ausgewählt`}</title>
					<polyline
						points="3.5,9 7,12.5 14.5,5"
						fill="none"
						stroke="white"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			)}
		</Box>
	);

	// Shared: preview image / fallback extension label
	const previewImage = (
		<Box
			sx={{
				width: 40,
				borderRadius: 1,
				overflow: 'hidden',
				backgroundColor: theme.palette.grey[100],
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center'
			}}
		>
			{isImageFile || item.previewUrl ? (
				<Box
					component="img"
					src={isImageFile ? (item.previewUrl ?? item.url) : item.previewUrl}
					alt={item.fileName}
					sx={{ width: '100%', height: 'auto', objectFit: 'contain' }}
				/>
			) : (
				<Typography variant="body-sm" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
					{extension}
				</Typography>
			)}
		</Box>
	);

	// Shared: meta text style (PDF | date)
	const metaTextSx = {
		display: 'block',
		color: '#353535',
		fontFamily: '"DIN Next LT Pro"',
		fontSize: '0.875rem',
		fontWeight: 400,
		lineHeight: '1rem',
		letterSpacing: '-0.05px',
		transform: 'translateY(1.5px)'
	} as const;

	// Shared: action icon button style
	const actionIconButtonSx = {
		color: theme.palette.text.secondary,
		p: 0.5,
		borderRadius: 1,
		transition: theme.transitions.create(['color', 'background-color'], {
			duration: theme.transitions.duration.shorter
		}),
		'&:hover': {
			color: theme.palette.primary.main,
			backgroundColor: 'transparent'
		},
		'&:active': { backgroundColor: 'transparent' }
	} as const;

	return (
		<Box
			sx={[
				{
					backgroundColor: theme.palette.common.white,
					border: `1px solid ${theme.palette.grey[200]}`,
					borderRadius: 2,
					width: showPreview ? '100%' : 240,
					maxWidth: showPreview ? { md: 632 } : 'none',
					height: { xs: 'auto', md: showPreview ? 160 : 168 },
					overflow: { xs: 'visible', md: showPreview ? 'hidden' : 'visible' },
					px: showPreview ? 6 : 4,
					pt: showPreview ? 6 : 4,
					pb: showPreview ? 6 : 2,
					display: 'flex',
					flexDirection: 'column',
					gap: showPreview ? 3 : 6,
					boxSizing: 'border-box',
					transition: theme.transitions.create(['border-color', 'box-shadow'], {
						duration: theme.transitions.duration.shorter
					}),
					'&:hover': {
						borderColor: theme.palette.primary.main
					}
				},
				...(Array.isArray(sx) ? sx : sx ? [sx] : [])
			]}
		>
			{showPreview ? (
				<React.Fragment>
					{/* ── Mobile layout ── */}
					<Box sx={{ display: { xs: 'block', md: 'none' }, minWidth: 0 }}>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								gap: 2
							}}
						>
							{/* Checkbox + meta text share a label so clicking either toggles */}
							<Box
								component="label"
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: 2,
									cursor: 'pointer',
									minWidth: 0
								}}
							>
								{hiddenCheckboxInput}
								{checkboxVisual}
								<Box component="span" sx={metaTextSx}>
									{metaLine}
								</Box>
							</Box>

							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: 3,
									flexShrink: 0
								}}
							>
								<IconButton
									size="small"
									onClick={handleDownload}
									aria-label={`${item.fileName} herunterladen`}
									sx={actionIconButtonSx}
								>
									<Icon name="download" />
								</IconButton>
								<IconButton
									size="small"
									onClick={handlePreview}
									aria-label={`${item.fileName} ansehen`}
									sx={actionIconButtonSx}
								>
									<Icon name="eye" />
								</IconButton>
							</Box>
						</Box>

						<Typography variant="body-sm" sx={{ color: theme.palette.text.primary, fontWeight: 500, mt: 6 }}>
							{item.fileName}
						</Typography>

						{item.description && (
							<Typography
								variant="body-sm"
								sx={{
									color: theme.palette.text.secondary,
									display: '-webkit-box',
									WebkitLineClamp: 2,
									WebkitBoxOrient: 'vertical',
									overflow: 'hidden',
									mt: 2
								}}
							>
								{item.description}
							</Typography>
						)}
					</Box>

					{/* ── Desktop layout (CSS grid) ──
					   ·  */}
					<Box
						sx={{
							display: { xs: 'none', md: 'grid' },
							gridTemplateColumns: '18px 40px 40px 2rem 1px 2rem 1fr auto',
							gridTemplateRows: 'auto auto',
							rowGap: { md: 2, lg: 3 },
							minWidth: 0,
							flex: 1
						}}
					>
						{/* row 1 – checkbox */}
						<Box
							component="label"
							sx={{
								gridColumn: 1,
								gridRow: 1,
								alignSelf: 'center',
								display: 'flex',
								alignItems: 'center',
								cursor: 'pointer'
							}}
						>
							{hiddenCheckboxInput}
							{checkboxVisual}
						</Box>

						{/* row 1 – meta text */}
						<Box
							component="span"
							sx={{
								gridColumn: 7,
								gridRow: 1,
								alignSelf: 'center',
								...metaTextSx
							}}
						>
							{metaLine}
						</Box>

						{/* row 1 – action icons */}
						<Box
							sx={{
								gridColumn: 8,
								gridRow: 1,
								alignSelf: 'center',
								display: 'flex',
								alignItems: 'center',
								gap: 3
							}}
						>
							<IconButton
								size="small"
								onClick={handleDownload}
								aria-label={`${item.fileName} herunterladen`}
								sx={actionIconButtonSx}
							>
								<Icon name="download" />
							</IconButton>
							<IconButton
								size="small"
								onClick={handlePreview}
								aria-label={`${item.fileName} ansehen`}
								sx={actionIconButtonSx}
							>
								<Icon name="eye" />
							</IconButton>
						</Box>

						{/* row 2 – image preview */}
						<Box sx={{ gridColumn: 3, gridRow: 2, alignSelf: 'stretch' }}>{previewImage}</Box>

						{/* row 2 – vertical divider */}
						<Box
							sx={{
								gridColumn: 5,
								gridRow: 2,
								alignSelf: 'stretch',
								width: '1px',
								background: 'rgba(0, 0, 0, 0.18)'
							}}
						/>

						{/* row 2 – title + description */}
						<Box
							sx={{
								gridColumn: 7,
								gridRow: 2,
								minWidth: 0,
								overflow: 'hidden'
							}}
						>
							<Typography
								variant="body-sm"
								sx={{
									color: theme.palette.text.primary,
									fontWeight: 500,
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									whiteSpace: 'nowrap'
								}}
							>
								{item.fileName}
							</Typography>

							{item.description && (
								<Typography
									variant="body-sm"
									sx={{
										color: theme.palette.text.secondary,
										display: '-webkit-box',
										WebkitLineClamp: 1,
										WebkitBoxOrient: 'vertical',
										overflow: 'hidden',
										mt: 1
									}}
								>
									{item.description}
								</Typography>
							)}
						</Box>
					</Box>
				</React.Fragment>
			) : (
				<React.Fragment>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							gap: 2
						}}
					>
						<Box component="label" sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
							{hiddenCheckboxInput}
							{checkboxVisual}
						</Box>

						<Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
							<IconButton
								size="small"
								onClick={handleDownload}
								aria-label={`${item.fileName} herunterladen`}
								sx={actionIconButtonSx}
							>
								<Icon name="download" />
							</IconButton>
							<IconButton
								size="small"
								onClick={handlePreview}
								aria-label={`${item.fileName} ansehen`}
								sx={actionIconButtonSx}
							>
								<Icon name="eye" />
							</IconButton>
						</Box>
					</Box>
					<Stack spacing={2} sx={{ minWidth: 0 }}>
						<Typography variant="body-sm" sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
							{item.fileName}
						</Typography>

						{item.description && (
							<Typography variant="body-sm" sx={{ color: theme.palette.text.secondary }}>
								{item.description}
							</Typography>
						)}
					</Stack>
				</React.Fragment>
			)}
		</Box>
	);
};

export default FileCard;
